/**
 * v2.0.1 Authorize wire mapping.
 *
 * The version-neutral `OcppAdapter.authorize(idTag)` takes only a flat
 * string. v2.0.1 wraps it as `{ idToken: { idToken: string, type:
 * IdTokenEnumType } }`. The `type` is chosen per station — Session 10b
 * accepts it at adapter construction and defaults to `'Central'` (free-
 * form CSMS-assigned identifier; matches v1.6 idTag semantics).
 *
 * AuthorizationStatus in v2.0.1 is a superset of v1.6's AuthStatus
 * (adds NoCredit, NotAllowedTypeEVSE, NotAtThisLocation, NotAtThisTime,
 * Unknown). Statuses outside the domain `AuthStatus` enum are mapped
 * down to the closest domain equivalent — see `toDomainAuthStatus`.
 */
import { AuthStatus } from '@/domain/types';
import type { AuthorizeResponse } from '@/protocol/OcppAdapter';
import type { OcspRequestData } from './pnc';

export type IdTokenTypeV201 =
  | 'Central'
  | 'eMAID'
  | 'ISO14443'
  | 'ISO15693'
  | 'KeyCode'
  | 'Local'
  | 'MacAddress'
  | 'NoAuthorization';

export const DEFAULT_ID_TOKEN_TYPE_V201: IdTokenTypeV201 = 'Central';

export type AuthorizationStatusV201 =
  | 'Accepted'
  | 'Blocked'
  | 'ConcurrentTx'
  | 'Expired'
  | 'Invalid'
  | 'NoCredit'
  | 'NotAllowedTypeEVSE'
  | 'NotAtThisLocation'
  | 'NotAtThisTime'
  | 'Unknown';

export function buildAuthorizeRequest(
  idTag: string,
  type: IdTokenTypeV201 = DEFAULT_ID_TOKEN_TYPE_V201,
): Record<string, unknown> {
  return {
    idToken: { idToken: idTag, type },
  };
}

/**
 * v2.0.1 has 10 authorization statuses; domain `AuthStatus` has 5.
 * v2.0.1-only statuses collapse to `Invalid` — they all mean "do not
 * start the transaction" from the simulator's perspective.
 */
export function toDomainAuthStatus(wire: AuthorizationStatusV201): AuthStatus {
  switch (wire) {
    case 'Accepted':
      return AuthStatus.Accepted;
    case 'Blocked':
      return AuthStatus.Blocked;
    case 'Expired':
      return AuthStatus.Expired;
    case 'ConcurrentTx':
      return AuthStatus.ConcurrentTx;
    case 'Invalid':
    case 'NoCredit':
    case 'NotAllowedTypeEVSE':
    case 'NotAtThisLocation':
    case 'NotAtThisTime':
    case 'Unknown':
      return AuthStatus.Invalid;
  }
}

// ── ISO 15118 Plug & Charge authorize (eMAID + contract certificate) ────────

/** AuthorizeResponse.certificateStatus — the CSMS's verdict on the EV cert. */
export type AuthorizeCertificateStatus =
  | 'Accepted'
  | 'SignatureError'
  | 'CertificateExpired'
  | 'CertificateRevoked'
  | 'NoCertificateAvailable'
  | 'CertChainError'
  | 'ContractCancelled';

export interface PncAuthorizeDomain {
  /** The EV's contract identifier (e-Mobility Account ID). */
  readonly eMAID: string;
  /** The EV's contract certificate chain as a PEM string (optional). */
  readonly certificate?: string;
  /** OCSP hash data for the cert chain (optional; 1–4 entries per schema). */
  readonly iso15118CertificateHashData?: readonly OcspRequestData[];
}

export interface PncAuthorizeResult {
  readonly authorizationStatus: AuthorizationStatusV201;
  /** Present when the CSMS validated the contract certificate. */
  readonly certificateStatus?: AuthorizeCertificateStatus;
}

/**
 * Build a Plug & Charge Authorize request: idToken typed `eMAID`, with the
 * optional contract `certificate` (PEM) and/or `iso15118CertificateHashData`
 * (OCSP). The simulator relays these verbatim; the CSMS does the real
 * validation and returns `certificateStatus`.
 */
export function buildPncAuthorizeRequest(req: PncAuthorizeDomain): Record<string, unknown> {
  return {
    idToken: { idToken: req.eMAID, type: 'eMAID' },
    ...(req.certificate !== undefined && req.certificate !== ''
      ? { certificate: req.certificate }
      : {}),
    ...(req.iso15118CertificateHashData && req.iso15118CertificateHashData.length > 0
      ? { iso15118CertificateHashData: req.iso15118CertificateHashData.map((h) => ({ ...h })) }
      : {}),
  };
}

export function parsePncAuthorizeResponse(payload: Record<string, unknown>): PncAuthorizeResult {
  const info = payload['idTokenInfo'] as Record<string, unknown> | undefined;
  const authorizationStatus = (info?.['status'] as AuthorizationStatusV201) ?? 'Invalid';
  const cs = payload['certificateStatus'];
  return {
    authorizationStatus,
    ...(typeof cs === 'string' ? { certificateStatus: cs as AuthorizeCertificateStatus } : {}),
  };
}

export function parseAuthorizeResponse(payload: Record<string, unknown>): AuthorizeResponse {
  const info = payload['idTokenInfo'] as Record<string, unknown> | undefined;
  if (!info) {
    return { idTagInfo: { status: AuthStatus.Invalid } };
  }
  const status = toDomainAuthStatus(info['status'] as AuthorizationStatusV201);
  const out: AuthorizeResponse = {
    idTagInfo: {
      status,
      ...(typeof info['cacheExpiryDateTime'] === 'string'
        ? { expiryDate: info['cacheExpiryDateTime'] }
        : {}),
      ...(typeof info['groupIdToken'] === 'object' && info['groupIdToken'] !== null
        ? {
            parentIdTag: (info['groupIdToken'] as Record<string, unknown>)['idToken'] as string,
          }
        : {}),
    },
  };
  return out;
}
